import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { requestCheckoutOtp, verifyCheckoutOtp } from "../services/orderPortal";
import { sessionMatchesEmail, setCustomerSession } from "../utils/customerSession";
import { isValidEmail } from "../utils/checkoutOrder";
import { BtnContent } from "../components/BtnIcon";
import { IoArrowBackOutline, IoMailOutline, IoShieldCheckmarkOutline } from "react-icons/io5";

function CheckoutVerify({ cart }) {
  const location = useLocation();
  const navigate = useNavigate();
  const customer = location.state?.customer;
  const email = String(customer?.email || "").trim();

  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  /** Avoid duplicate auto-send when React re-runs the effect for the same email. */
  const otpSentForEmail = useRef("");

  const goCheckout = useCallback(
    (cust) => {
      navigate("/checkout", { replace: true, state: { customer: cust } });
    },
    [navigate],
  );

  useEffect(() => {
    setOtp("");
    setError(null);
    setInfo(null);
  }, [email]);

  const sendCode = useCallback(async () => {
    if (!isValidEmail(email)) return;
    setError(null);
    setInfo(null);
    setSending(true);
    const res = await requestCheckoutOtp(email);
    setSending(false);
    if (!res.ok) {
      setError(res.message || "Could not send code.");
      return;
    }
    setInfo(res.data?.message || "Check your email for the 6-digit code.");
  }, [email]);

  useEffect(() => {
    if (cart.length === 0) {
      navigate("/cart", { replace: true });
      return;
    }
    if (!customer || typeof customer !== "object" || !isValidEmail(email)) {
      navigate("/cart", { replace: true });
      return;
    }
    if (sessionMatchesEmail(email)) {
      goCheckout({
        name: customer.name ?? "",
        email,
        phone: customer.phone ?? "",
        address: customer.address ?? "",
        pincode: customer.pincode ?? "",
      });
      return;
    }
    if (otpSentForEmail.current === email) return;
    otpSentForEmail.current = email;
    sendCode();
  }, [cart.length, customer, email, navigate, goCheckout, sendCode]);

  const onVerify = async () => {
    setError(null);
    const code = otp.replace(/\D/g, "");
    if (code.length !== 6) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setVerifying(true);
    const res = await verifyCheckoutOtp(email, code);
    setVerifying(false);
    if (!res.ok || !res.data?.sessionToken) {
      setError(res.message || "Invalid or expired code.");
      return;
    }
    setCustomerSession(res.data.sessionToken, res.data.email || email);
    goCheckout({
      name: customer.name ?? "",
      email,
      phone: customer.phone ?? "",
      address: customer.address ?? "",
      pincode: customer.pincode ?? "",
    });
  };

  if (cart.length === 0 || !customer || !isValidEmail(email)) {
    return null;
  }

  return (
    <div className="container py-5" style={{ maxWidth: "440px" }}>
      <h1 className="h4 mb-3 text-center">Verify your email</h1>
      <p className="small text-muted text-center mb-4">
        We sent a one-time code to <strong>{email}</strong>. Enter it below to continue to checkout.
      </p>

      <div className="mb-3">
        <label className="form-label small fw-semibold" htmlFor="checkout-verify-otp">
          6-digit code
        </label>
        <input
          id="checkout-verify-otp"
          type="text"
          inputMode="numeric"
          maxLength={6}
          className="form-control text-center fs-4"
          style={{ letterSpacing: "0.35em" }}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="000000"
          autoComplete="one-time-code"
        />
      </div>

      <button
        type="button"
        className="btn btn-primary w-100 py-2 mb-2"
        disabled={verifying}
        onClick={onVerify}
      >
        <BtnContent icon={IoShieldCheckmarkOutline}>
          {verifying ? "Verifying…" : "Continue to checkout"}
        </BtnContent>
      </button>

      <button
        type="button"
        className="btn btn-outline-secondary w-100 py-2 mb-3"
        disabled={sending}
        onClick={() => sendCode()}
      >
        <BtnContent icon={IoMailOutline}>{sending ? "Sending…" : "Resend code"}</BtnContent>
      </button>

      {error && <p className="small text-danger mb-0">{error}</p>}
      {info && !error && <p className="small text-success mb-0">{info}</p>}

      <p className="text-center mt-4 mb-0">
        <button
          type="button"
          className="btn btn-link checkout-back-to-cart"
          onClick={() =>
            navigate("/cart", {
              replace: true,
              state: { customer },
            })
          }
        >
          <BtnContent icon={IoArrowBackOutline}>Back to cart</BtnContent>
        </button>
      </p>
    </div>
  );
}

export default CheckoutVerify;
